import { TextFieldModule } from '@angular/cdk/text-field';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TranslateModule } from '@ngx-translate/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { Observable } from 'rxjs';

import { MimeTypesEnum, ThumbnailsDict } from '@models/module/fields/main';
import { CipoImgBase64 } from '@shared/components/img-base64/img-base64.component';
import { CipoDialogHeaderComponent } from '@shared/dialogs';

export interface NoteDialogFile {
  name: string;
  mimeType?: MimeTypesEnum;
  thumbnail200?: string;
}

export interface NoteDialogData {
  file: NoteDialogFile;
  /** Note already at hand. Leave unset and pass `note$` when it has to be fetched first. */
  note?: string;
  /** Resolves the stored note; the field shows a skeleton until it emits. */
  note$?: Observable<string>;
}

export interface NoteDialogResult {
  note: string;
}

const NOTE_MAX_LENGTH = 1000;

@Component({
  selector: 'cipo-note-dialog',
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    TextFieldModule,
    FormsModule,
    FlexLayoutModule,
    TranslateModule,
    CipoDialogHeaderComponent,
    CipoImgBase64,
    NgxSkeletonLoaderModule,
  ],
  templateUrl: './note-dialog.component.html',
  styles: `
    .file-chip__preview ::ng-deep img {
      display: block;
      object-fit: cover;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NoteDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<NoteDialogComponent, NoteDialogResult>);
  protected readonly data = inject<NoteDialogData>(MAT_DIALOG_DATA);

  protected readonly maxLength = NOTE_MAX_LENGTH;
  protected readonly note = signal(this.data.note ?? '');
  protected readonly loading = signal(!!this.data.note$);
  protected readonly title = signal(this.data.note$ ? 'general.loading' : this.noteTitle(this.data.note));

  constructor() {
    this.data.note$?.pipe(takeUntilDestroyed()).subscribe({
      next: note => {
        this.note.set(note ?? '');
        this.title.set(this.noteTitle(note));
        this.loading.set(false);
      },
      error: () => this.loading.set(false),
    });
  }

  private noteTitle(note?: string): string {
    return note ? 'fileExplorer.editNote' : 'fileExplorer.addNote';
  }

  protected fileTypeIcon(): string {
    return ThumbnailsDict[this.data.file.mimeType as keyof typeof ThumbnailsDict] ?? ThumbnailsDict.default;
  }

  protected save() {
    this.dialogRef.close({ note: this.note().trim() });
  }
}
